import { BsAirplane, BsTruck } from "react-icons/bs";
import { FaShip } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  currencySymbol,
  normaliseBrackets,
} from "../../data/deliveryTariffs";

/**
 * Generic warehouse-address card, used for any abroad office that doesn't have a
 * country-specific layout (currently only China). Renders whatever fields are populated.
 * The user's MGL id is appended to Name/Surname so the warehouse can route the
 * package on arrival — matches the "ოთახის ნომერი" convention.
 *
 * For home_delivery_only offices (USA, Greece) the "Price" row is replaced with
 * a scroll-to-pricing button — the flat per-kg price doesn't apply, real pricing
 * comes from the HomeDeliveryPriceTable rendered lower in the same card.
 */
export default function GenericContent({
  item,
  user,
  currentCountryName,
  // onScrollToPricing kept for API compatibility with old callers; no longer used
  // since the address page shows just the per-kg USD line inline.
  // eslint-disable-next-line no-unused-vars
  onScrollToPricing,
}) {
  const { t } = useTranslation();
  const mglTag = user?.id ? `MGL${user.id}` : "";
  // The flat price never applies to a home-delivery office — its tariff does.
  const hasRealPrice =
    !item?.home_delivery_only &&
    item?.price !== null &&
    item?.price !== "" &&
    Number(item?.price) > 0;

  // Home-delivery offices (USA, Greece) carry a delivery_tariff JSON with a
  // single brackets[] table. Surface BOTH per-kg rates from the first
  // (≤ included_kg) bracket — that's what the operator wants advertised at
  // the top of the card. Highland falls back to Tbilisi when the bracket
  // doesn't carry its own rate.
  const homeDeliveryBrackets =
    item?.home_delivery_only ? normaliseBrackets(item.delivery_tariff) : [];
  const firstBracket = homeDeliveryBrackets[0] ?? null;
  const perKgTbilisi = firstBracket
    ? Number(firstBracket.transport_per_kg)
    : null;
  const perKgHighland = firstBracket
    ? Number(
        firstBracket.transport_per_kg_highland ?? firstBracket.transport_per_kg
      )
    : null;

  return (
    <>
      {item?.country && (
        <div className="p-4 border-b border-gray-200 flex gap-2 items-center">
          {item?.type === 1 && <BsTruck className="text-2xl text-teal_600" />}
          {item?.type === 2 && <BsAirplane className="text-2xl text-teal_600" />}
          {item?.type === 3 && <FaShip className="text-2xl text-teal_600" />}
          <div className="flex gap-2 items-center sm:flex-col sm:items-start">
            <p className="text-gray-500 text-sm md:text-xs font-semibold">
              Warehouse:
            </p>
            <p className="font-semibold text-sm md:text-xs">{item.country}</p>
          </div>
          {hasRealPrice && (
            <div className="flex gap-2 items-center sm:flex-col sm:items-start ml-auto">
              <p className="text-gray-500 text-sm md:text-xs font-semibold">
                Price:
              </p>
              <p className="font-semibold text-sm md:text-xs">
                {item?.price}
                {item?.currency ? ` ${item.currency}` : "$"}
              </p>
            </div>
          )}
          {perKgTbilisi != null && (
            <div className="ml-auto flex gap-2 items-baseline sm:ml-0 flex-wrap">
              <span className="flex items-baseline gap-1">
                <span className="text-gray-500 text-[11px] md:text-[10px]">
                  {t("tbilisi_and_regions") || "თბილისი / რეგიონები"}:
                </span>
                <span className="font-bold text-teal_600 text-sm md:text-xs">
                  {perKgTbilisi.toFixed(2)}{" "}
                  {currencySymbol(item.currency)} / {t("admin_kg") || "კგ"}
                </span>
              </span>
              <span className="flex items-baseline gap-1">
                <span className="text-gray-500 text-[11px] md:text-[10px]">
                  {t("highland") || "მაღალმთიანი"}:
                </span>
                <span className="font-bold text-teal_600 text-sm md:text-xs">
                  {perKgHighland.toFixed(2)}{" "}
                  {currencySymbol(item.currency)} / {t("admin_kg") || "კგ"}
                </span>
              </span>
            </div>
          )}
        </div>
      )}

      <div className="p-4 border-b border-gray-200 flex gap-2 items-center sm:flex-col sm:items-start">
        <p className="text-gray-500 text-sm md:text-xs font-semibold">
          Name / Surname:
        </p>
        <p className="font-semibold text-sm md:text-xs">
          {user?.fname_eng
            ? `${user.fname_eng} ${user.lname_eng ?? ""} ${mglTag}`
            : `${user?.cname ?? ""} ${mglTag}`}
        </p>
      </div>

      {currentCountryName && (
        <div className="p-4 border-b border-gray-200 flex gap-2 items-center sm:flex-col sm:items-start">
          <p className="text-gray-500 text-sm md:text-xs font-semibold">
            Country:
          </p>
          <p className="font-semibold text-sm md:text-xs">{currentCountryName}</p>
        </div>
      )}

      {item?.state && (
        <div className="p-4 border-b border-gray-200 flex gap-2 items-center sm:flex-col sm:items-start">
          <p className="text-gray-500 text-sm md:text-xs font-semibold">
            Region / State:
          </p>
          <p className="font-semibold text-sm md:text-xs">{item.state}</p>
        </div>
      )}

      {item?.town && (
        <div className="p-4 border-b border-gray-200 flex gap-2 items-center sm:flex-col sm:items-start">
          <p className="text-gray-500 text-sm md:text-xs font-semibold">
            City:
          </p>
          <p className="font-semibold text-sm md:text-xs">{item.town}</p>
        </div>
      )}

      {item?.adress1 && (
        <div className="p-4 border-b border-gray-200 flex gap-2 items-center sm:flex-col sm:items-start">
          <p className="text-gray-500 text-sm md:text-xs font-semibold">
            Address 1:
          </p>
          <p className="font-semibold text-sm md:text-xs">{item.adress1}</p>
        </div>
      )}

      <div className="p-4 border-b border-gray-200 flex gap-2 items-center sm:flex-col sm:items-start">
        <p className="text-gray-500 text-sm md:text-xs font-semibold">
          Address 2:
        </p>
        <p className="font-semibold text-sm md:text-xs">{mglTag}</p>
      </div>

      {item?.phone && (
        <div className="p-4 flex gap-2 border-b border-gray-200 items-center sm:flex-col sm:items-start">
          <p className="text-gray-500 text-sm md:text-xs font-semibold">
            Phone:
          </p>
          <p className="font-semibold text-sm md:text-xs">{item.phone}</p>
        </div>
      )}

      {item?.zip_code && (
        <div className="p-4 flex gap-2 items-center sm:flex-col sm:items-start">
          <p className="text-gray-500 text-sm md:text-xs font-semibold">
            Postal Code:
          </p>
          <p className="font-semibold text-sm md:text-xs">{item.zip_code}</p>
        </div>
      )}
    </>
  );
}
